<div class="eventq-select__root">
  @if (searchable()) {
    <div
      #trigger
      class="eventq-select__trigger eventq-select__trigger--searchable"
      [class.eventq-select__trigger--bare]="bareTrigger()"
      [class.eventq-select__trigger--invalid]="showValidationError()"
      [class.eventq-select__trigger--danger]="tone() === 'danger'"
      [attr.aria-expanded]="menuOpen()"
      aria-haspopup="listbox"
    >
      @if (label()) {
        <span class="eventq-select__field-label-notch">
          <span class="eventq-select__field-label">{{ label() }}</span>
          @if (requiredIndicator()) {
            <span class="eventq-select__field-required" aria-hidden="true">*</span>
          }
        </span>
      }
      @if (leadingIconSrc()) {
        <img class="eventq-select__leading-icon" [src]="leadingIconSrc()! | prefixUrl" alt="" />
      }
      <input
        type="text"
        class="eventq-select__trigger-input"
        [value]="draftText()"
        [placeholder]="placeholder()"
        [disabled]="disabled()"
        [attr.aria-labelledby]="labelledBy()"
        [attr.aria-label]="label() || null"
        [attr.aria-invalid]="showValidationError()"
        [attr.aria-required]="requiredIndicator() ? true : null"
        [attr.aria-expanded]="menuOpen()"
        role="combobox"
        autocomplete="off"
        (input)="onTriggerInput($event)"
        (keydown)="onTriggerKeydown($event)"
        (focus)="onTriggerFocus($event)"
        (click)="onTriggerFocus($event)"
      />
      @if (badgeCount() !== null) {
        <span class="eventq-select__badge" aria-hidden="true">{{ badgeCount() }}</span>
      }
      @if (!hideChevron()) {
        <button
          type="button"
          class="eventq-select__chevron-btn"
          tabindex="-1"
          aria-hidden="true"
          [disabled]="disabled()"
          (click)="toggleMenu($event)"
        >
          <mat-icon class="eventq-select__chevron">arrow_drop_down</mat-icon>
        </button>
      }
    </div>
  } @else {
    <button
      #trigger
      type="button"
      class="eventq-select__trigger"
      [class.eventq-select__trigger--bare]="bareTrigger()"
      [class.eventq-select__trigger--invalid]="showValidationError()"
      [class.eventq-select__trigger--danger]="tone() === 'danger'"
      [disabled]="disabled()"
      [attr.aria-expanded]="menuOpen()"
      aria-haspopup="listbox"
      [attr.aria-labelledby]="labelledBy()"
      [attr.aria-label]="label() || null"
      [attr.aria-invalid]="showValidationError()"
      [attr.aria-required]="requiredIndicator() ? true : null"
      (click)="toggleMenu($event)"
    >
      @if (label()) {
        <span class="eventq-select__field-label-notch">
          <span class="eventq-select__field-label">{{ label() }}</span>
          @if (requiredIndicator()) {
            <span class="eventq-select__field-required" aria-hidden="true">*</span>
          }
        </span>
      }
      @if (leadingIconSrc()) {
        <img class="eventq-select__leading-icon" [src]="leadingIconSrc()! | prefixUrl" alt="" />
      }
      <span
        class="eventq-select__value"
        [class.eventq-select__value--placeholder]="!selectedLabel()"
      >
        {{ selectedLabel() ?? placeholder() }}
      </span>
      @if (badgeCount() !== null) {
        <span class="eventq-select__badge" aria-hidden="true">{{ badgeCount() }}</span>
      }
      @if (!hideChevron()) {
        <mat-icon class="eventq-select__chevron" aria-hidden="true">arrow_drop_down</mat-icon>
      }
    </button>
  }

  @if (showValidationError() && validationMessage()) {
    <p class="eventq-select__error">{{ validationMessage() }}</p>
  }

  @if (menuOpen()) {
    <ul
      #menu
      class="eventq-select__menu"
      [class.eventq-select__menu--content]="menuWidth() === 'content'"
      [class.eventq-select__menu--grouped]="groups().length > 0"
      role="listbox"
      [attr.aria-labelledby]="labelledBy()"
      [style.top]="menuStyle()?.top"
      [style.bottom]="menuStyle()?.bottom"
      [style.left]="menuStyle()?.left"
      [style.width]="menuStyle()?.width"
    >
      @if (showNoMatches()) {
        <li class="eventq-select__empty" role="presentation">No matches found</li>
      } @else if (groups().length > 0) {
        @for (group of filteredGroups(); track group.id) {
          <li
            class="eventq-select__group"
            role="presentation"
            [style.background]="group.background"
          >
            <ul class="eventq-select__group-list" role="group">
              @for (option of group.options; track option.id) {
                <li>
                  <button
                    type="button"
                    class="eventq-select__option"
                    role="option"
                    [attr.data-option-id]="option.id"
                    [attr.aria-selected]="isSelected(option.id)"
                    [class.eventq-select__option--selected]="isSelected(option.id)"
                    [class.eventq-select__option--active]="isActiveOption(option.id)"
                    [class.eventq-select__option--accented]="isSelected(option.id) && !!option.color"
                    [style.color]="isSelected(option.id) ? (option.color ?? null) : null"
                    (click)="onSelect(option.id, $event)"
                  >
                    @if (multiple() && !option.iconSrc) {
                      <span class="eventq-select__check" aria-hidden="true">
                        {{ isSelected(option.id) ? '✓' : '' }}
                      </span>
                    }
                    @if (option.iconSrc) {
                      <span class="eventq-select__option-icon" aria-hidden="true">
                        <img [src]="option.iconSrc | prefixUrl" alt="" />
                      </span>
                    }
                    <span class="eventq-select__option-label">{{ option.label }}</span>
                  </button>
                </li>
              }
            </ul>
          </li>
        }
      } @else {
        @for (option of filteredOptions(); track option.id) {
          <li>
            <button
              type="button"
              class="eventq-select__option"
              role="option"
              [attr.data-option-id]="option.id"
              [attr.aria-selected]="isSelected(option.id)"
              [class.eventq-select__option--selected]="isSelected(option.id)"
              [class.eventq-select__option--active]="isActiveOption(option.id)"
              [class.eventq-select__option--accented]="isSelected(option.id) && !!option.color"
              [style.color]="isSelected(option.id) ? (option.color ?? null) : null"
              (click)="onSelect(option.id, $event)"
            >
              @if (multiple() && !option.iconSrc) {
                <span class="eventq-select__check" aria-hidden="true">
                  {{ isSelected(option.id) ? '✓' : '' }}
                </span>
              }
              @if (option.iconSrc) {
                <span class="eventq-select__option-icon" aria-hidden="true">
                  <img [src]="option.iconSrc | prefixUrl" alt="" />
                </span>
              }
              <span class="eventq-select__option-label">{{ option.label }}</span>
            </button>
          </li>
        }
      }
    </ul>
  }
</div>
